/* /cases — disciplinary cases & grievances with the rules applied. */
import "./pay.css";
import { useState } from "react";
import { useCases, useToday } from "../../data/hooks";
import { daysTo } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Button, Card, EmptyState, ErrorBox, Icon, PageHeader, Person, Seg, Skeleton, useToast } from "../../ui";
import { StatusChip, unknownEmp, useEmpMap } from "./components/common";

export default function Cases() {
  const { L, t, lang } = useI18n();
  const toast = useToast();
  const today = useToday();
  const q = useCases();
  const emps = useEmpMap();
  const [kind, setKind] = useState<"all" | "Disciplinary" | "Grievance">("all");
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const list = q.data.filter((c) => kind === "all" || c.kind === kind);
  return (
    <>
      <PageHeader title={L("Disciplinary & grievances", "الجزاءات والتظلمات")} sub={L("Run cases by the book: investigation, written decision and the employee's right to object.", "أدر الحالات وفق النظام: تحقيق وقرار مكتوب وحق الاعتراض.")}
        actions={<Button variant="pri" icon="plus" onClick={() => toast(L("Case form", "نموذج الحالة"))} data-testid="btn-open-case">{L("Open a case", "فتح حالة")}</Button>} />
      <div className="g g-2-1">
        <section className="card">
          <div className="toolbar"><Seg label={t("Type")} value={kind} onChange={setKind} items={[["all", L("All", "الكل")], ["Disciplinary", L("Disciplinary", "جزاء")], ["Grievance", L("Grievance", "تظلم")]]} /></div>
          {list.length === 0 ? <EmptyState title={L("No cases", "لا توجد حالات")} /> : (
            <div className="tw">
              <table className="tbl cards" data-testid="cases-table">
                <thead><tr><th>{t("Employee")}</th><th>{L("Case", "الحالة")}</th><th>{L("Deadline", "المهلة")}</th><th>{t("Status")}</th></tr></thead>
                <tbody>
                  {list.map((c) => {
                    const dl = daysTo(today, c.due);
                    return (
                      <tr key={c.id} data-testid={"case-row-" + c.id}>
                        <td className="k1"><Person e={emps.map.get(c.employeeId) ?? unknownEmp(c.employeeId)} /></td>
                        <td className="k3" data-l=""><span className={"chip " + (c.kind === "Grievance" ? "info" : "warn")}>{L(c.kind, c.kind === "Grievance" ? "تظلم" : "جزاء")}</span> {c.title}<div className="xs muted">{c.step} · {L("opened ", "فُتحت ")}{gDate(c.opened, lang)}</div></td>
                        <td className="x" data-l="">{gDate(c.due, lang)} <span className={"xs " + (dl < 0 ? "" : "muted")} style={dl < 0 ? { color: "var(--danger)" } : undefined} data-testid={"case-days-" + c.id}>({dl}{L("d", " يوم")})</span></td>
                        <td className="k2"><StatusChip status={c.status} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <Card title={L("Rules applied", "القواعد المطبقة")}>
          <div className="stack sm">
            {[
              L("Penalties must follow your approved internal work regulations.", "يجب أن تتبع الجزاءات لائحة تنظيم العمل المعتمدة."),
              L("Fines are capped at 5 days' wage per month.", "الغرامة بحد أقصى أجر 5 أيام في الشهر."),
              L("Suspension without pay: up to 5 days a month.", "الإيقاف دون أجر حتى 5 أيام في الشهر."),
              L("The employee may object within 30 days of being notified.", "يحق للموظف الاعتراض خلال 30 يومًا من إبلاغه."),
              L("Grievances: employee raises within 30 days; employer responds within 15 days.", "التظلم خلال 30 يومًا والرد خلال 15 يومًا."),
            ].map((x) => <div key={x} className="row nw" style={{ alignItems: "flex-start" }}><Icon name="check" className="s" /><span>{x}</span></div>)}
          </div>
        </Card>
      </div>
    </>
  );
}
